import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import jwt from "jwt-decode";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const tokenKey = "token";

const slice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    loading: false,
    userDetails: null,
    userDetailsLoadingId: null,
    userDetailsLoadedId: null,
    updateStatus: null,
    changePasswordStatus: null,
    changeEmailStatus: null,
    verifyUserStatus: null,
    forgotPasswordStatus: null,
    adminDetails: null,
    hostDetails: null,
    updateddatarecieved: null,
    emailOtpSentStatus: "",
    verifyOtpStatus: "",
    validateStatus: false,
    loginFailedStatus: "",
    picUpdatedStatus: false,
    profilePic: null,
    profilePicTwo: null,
    logoutStatus: null,
    loginLogoutStatus: null,
    clientDetails: null,
    requests: {
      login: createRequestState(),
      userDetails: createRequestState(),
      rolePermission: createRequestState(),
      profilePic: createRequestState(),
      profilePicTwo: createRequestState(),
      updateProfile: createRequestState(),
      changePassword: createRequestState(),
      changeEmail: createRequestState(),
      verifyUser: createRequestState(),
      forgotPassword: createRequestState(),
      clientDetails: createRequestState(),
      logout: createRequestState(),
    }
  },
  reducers: {
    authRequestStarted: (auth, action) => {
      requestStarted(auth.requests, action.payload);
    },
    authRequestFailed: (auth, action) => {
      requestFailed(auth.requests, action.payload?.key, action.payload?.error || action.payload);
    },
    login: (auth, action) => {
      auth.user = action.payload;
      auth.token = null;
      auth.loginFailedStatus = "";
      if (action.payload?.refreshToken) {
        localStorage.setItem("refreshToken", action.payload.refreshToken);
      }
      localStorage.removeItem("challengeId");
      requestSucceeded(auth.requests, "login");
    },
    loginFailed: (auth, action) => {
      auth.loginFailedStatus = action.payload;
      auth.user = null;
      auth.token = null;
      requestFailed(auth.requests, "login", action.payload);
    },
    emailOtpReceived: (auth, action) => {
      auth.emailOtpSentStatus = action.payload;
      auth.verifyOtpStatus = "";
    },
    verifiedOtp: (auth, action) => {
      auth.verifyOtpStatus = action.payload;
      auth.emailOtpSentStatus = "";
    },
    verifiedOtpFailed: (auth, action) => {
      auth.verifyOtpStatus = "";
      auth.emailOtpSentStatus = "";
    },
    validateUser: (auth, action) => {
      auth.user = action.payload;
      auth.token = action.payload?.token;
      auth.validateStatus = true;
      localStorage.setItem(tokenKey, action.payload?.token);
      localStorage.removeItem("tempPwd");
    },
    hostDetailsAdded: (auth, action) => {
      auth.hostDetails = action.payload;
      localStorage.setItem("hostId", action.payload);
    },
    loginStatusDataUpdated: (auth, action) => {
      auth.updateddatarecieved = action.payload;
    },
    logout: (auth, action) => {
      auth.user = null;
      auth.token = null;
      auth.hostDetails = null;
      auth.validateStatus = false;
      localStorage.removeItem(tokenKey);
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("challengeId");
    },
    refresh: (auth, action) => {
      const token = action.payload?.accessToken || action.payload?.token;
      auth.token = token;
      if (token) localStorage.setItem(tokenKey, token);
      if (action.payload?.refreshToken) {
        localStorage.setItem("refreshToken", action.payload.refreshToken);
      }
    },
    setLoginUserType: (auth, action) => {
      auth.user = { ...auth.user, ...action.payload };
    },
    userDetailsRequested: (auth, action) => {
      auth.userDetailsLoadingId = action.payload;
    },
    loggedUserDetailsReceived: (auth, action) => {
      auth.userDetails = action.payload;
      auth.userDetailsLoadingId = null;
      auth.userDetailsLoadedId = action.payload?.performerid || action.payload?.performerId || action.payload?.id || null;
      auth.updateStatus = null;
      requestSucceeded(auth.requests, "userDetails");
    },
    userDetailsRequestFailed: (auth, action) => {
      auth.userDetailsLoadingId = null;
      requestFailed(auth.requests, "userDetails", action.payload);
    },
    userDetailsUpdated: (auth, action) => {
      auth.updateStatus = action.payload;
      auth.userDetails = null;
      auth.userDetailsLoadedId = null;
      requestSucceeded(auth.requests, "updateProfile", action.payload);
    },
    changePasswordReceived: (auth, action) => {
      auth.changePasswordStatus = action.payload;
      requestSucceeded(auth.requests, "changePassword", action.payload);
    },
    changeEmailReceived: (auth, action) => {
      auth.changeEmailStatus = action.payload;
      requestSucceeded(auth.requests, "changeEmail", action.payload);
    },
    verifyUserReceived: (auth, action) => {
      auth.verifyUserStatus = action.payload;
      requestSucceeded(auth.requests, "verifyUser", action.payload);
    },
    forgotPasswordReceived: (auth, action) => {
      auth.forgotPasswordStatus = action.payload;
      requestSucceeded(auth.requests, "forgotPassword", action.payload);
    },
    adminDetailsReceived: (auth, action) => {
      auth.adminDetails = action.payload;
    },
    rolePermissionReceived: (auth, action) => {
      requestSucceeded(auth.requests, "rolePermission");
    },
    userProfilePicUpdated: (auth, action) => {
      auth.picUpdatedStatus = action.payload;
    },
    profilePicReceived: (auth, action) => {
      auth.profilePic = action.payload;
      auth.picUpdatedStatus = false;
      requestSucceeded(auth.requests, "profilePic");
    },
    profilePicReceivedTwo: (auth, action) => {
      auth.profilePicTwo = action.payload;
      auth.picUpdatedStatus = false;
      requestSucceeded(auth.requests, "profilePicTwo");
    },
    userLogoutStatusRecevied: (auth, action) => {
      auth.logoutStatus = action.payload;
      auth.user = null;
      auth.token = null;
      auth.hostDetails = null;
      auth.validateStatus = false
      localStorage.removeItem("hostId");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("challengeId");
      requestSucceeded(auth.requests, "logout");
    },
    loginLogoutStatusReceived: (auth, action) => {
      auth.loginLogoutStatus = action.payload;
      localStorage.removeItem(tokenKey);
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("challengeId");
      localStorage.removeItem("userType");
    },
    clientDetailsRecevied: (auth, action) => {
      auth.clientDetails = action.payload;
      requestSucceeded(auth.requests, "clientDetails");
    },
  },
});

export const { authRequestStarted, authRequestFailed, login, logout, refresh, setLoginUserType,
  userDetailsRequested, loggedUserDetailsReceived, userDetailsRequestFailed, userDetailsUpdated,
  changePasswordReceived, changeEmailReceived, verifyUserReceived, forgotPasswordReceived, adminDetailsReceived,
  rolePermissionReceived,
  emailOtpReceived, verifiedOtp, validateUser, verifiedOtpFailed, loginFailed,
  hostDetailsAdded, loginStatusDataUpdated, userLogoutStatusRecevied, loginLogoutStatusReceived,
  userProfilePicUpdated, profilePicReceived, profilePicReceivedTwo, clientDetailsRecevied } = slice.actions;
export default slice.reducer;

export const sign = (email, password) =>
  apiCallBegan({
    url: config.AuthPassword || "Auth/password",
    method: "post",
    data: {
      username: email,
      password: password,
      challengeId: localStorage.getItem("challengeId")
    },
    onSuccess: login.type,
    onError: loginFailed.type,
    withCredentials: true,
    silentError: true,
  });

export const loginUser = (username, password, userType = "admin", partnerId = 0) => async (dispatch) => {
  const user = await dispatch(apiCallBegan({
    url: config.AuthPassword || "Auth/password",
    method: "post",
    data: {
      username,
      password,
      challengeId: localStorage.getItem("challengeId"),
    },
    onStart: { type: authRequestStarted.type, payload: "login" },
    onSuccess: login.type,
    onError: loginFailed.type,
    withCredentials: true,
    throwOnError: true,
    silentError: true,
  }));

  if (user) {
    localStorage.setItem("currentUser", JSON.stringify(user));
    if (user.refreshToken) localStorage.setItem("refreshToken", user.refreshToken);
  }
  return user;
};

export const validate = (userId, email, pwd) =>
  apiCallBegan({
    url: config.ValidateUser,
    method: "post",
    data: {
      userId: userId,
      email: email,
      userType: "admin",
      password: pwd
    },
    onSuccess: validateUser.type,
    withCredentials: true,
  });

export const validateAdUser = (userId, token) =>
  apiCallBegan({
    url: config.ValidateAdUser,
    method: "post",
    data: {
      userGuid: userId,
      userToken: token,
    },
    onSuccess: validateUser.type,
    withCredentials: true,
  });

export const getRefreshToken = () =>
  apiCallBegan({
    url: config.AuthRefresh || "Auth/refresh",
    method: "post",
    data: { refreshToken: localStorage.getItem("refreshToken") },
    onSuccess: refresh.type,
    withCredentials: true,
    onError: logout.type,
  });

export const logoutCurrentUser = (id, hostId) =>
  apiCallBegan({
    url: config.LogoutUser,
    method: "post",
    data: { id: id, hostId: hostId },
    onSuccess: userLogoutStatusRecevied.type
  });

export const loadUserDetails = (id) => (dispatch, getState) => {
  const { auth } = getState();
  const requestedId = String(id);
  const loadedId = String(auth.userDetailsLoadedId || auth.userDetails?.performerid || auth.userDetails?.performerId || auth.userDetails?.id || "");

  if (loadedId === requestedId) return auth.userDetails;

  dispatch(userDetailsRequested(id));
  return dispatch(apiCallBegan({
    url: `${config.GetUser}${id}`,
    onStart: { type: authRequestStarted.type, payload: "userDetails" },
    onSuccess: loggedUserDetailsReceived.type,
    onError: userDetailsRequestFailed.type,
    cache: true,
    cacheDuration: 60000
  }));
};

export const loadRolePermission = (roleId, roleSource) =>
  apiCallBegan({
    url: `${config.GetRolePermission}${roleId}${roleSource ? `&roleSource=${roleSource}` : ""}`,
    onStart: { type: authRequestStarted.type, payload: "rolePermission" },
    onSuccess: rolePermissionReceived.type,
    onError: { type: authRequestFailed.type, payload: { key: "rolePermission" } },
    cache: true,
    cacheDuration: 300000
  });

export const changePassword = (oldPassword, newPassword, userId) => apiCallBegan({
  url: config.ChangePassword,
  method: "post",
  data: {
    oldPassword,
    newPassword,
    userId,
  },
  onStart: { type: authRequestStarted.type, payload: "changePassword" },
  onSuccess: changePasswordReceived.type,
  onError: { type: authRequestFailed.type, payload: { key: "changePassword" } },
  throwOnError: true,
});

export const changeEmail = (email, userId) => apiCallBegan({
  url: "Account/ChangeEmail",
  method: "post",
  data: {
    email,
    userId,
  },
  onStart: { type: authRequestStarted.type, payload: "changeEmail" },
  onSuccess: changeEmailReceived.type,
  onError: { type: authRequestFailed.type, payload: { key: "changeEmail" } },
  throwOnError: true,
});

export const verifyUser = (data) => apiCallBegan({
  url: "Account/VerifyUser",
  method: "post",
  data,
  onStart: { type: authRequestStarted.type, payload: "verifyUser" },
  onSuccess: verifyUserReceived.type,
  onError: { type: authRequestFailed.type, payload: { key: "verifyUser" } },
  throwOnError: true,
});

export const forgotPassword = (data) => apiCallBegan({
  url: "Account/ForgotPassoword",
  method: "post",
  data,
  onStart: { type: authRequestStarted.type, payload: "forgotPassword" },
  onSuccess: forgotPasswordReceived.type,
  onError: { type: authRequestFailed.type, payload: { key: "forgotPassword" } },
  throwOnError: true,
});

export const loadAdminDetails = (adminId) => apiCallBegan({
  url: `Account/GetAdminDetails?adminId=${adminId}`,
  onSuccess: adminDetailsReceived.type,
});

export const loadUserProfilePic = (id) => apiCallBegan({
  url: `${config.GetProfilePicData}${id}`,
  onStart: { type: authRequestStarted.type, payload: "profilePic" },
  onSuccess: profilePicReceived.type,
  onError: { type: authRequestFailed.type, payload: { key: "profilePic" } },
  cache: true,
  cacheDuration: 300000
})
export const loadUserProfilePicTwo = (id) => apiCallBegan({
  url: `${config.GetProfilePicData}${id}`,
  onStart: { type: authRequestStarted.type, payload: "profilePicTwo" },
  onSuccess: profilePicReceivedTwo.type,
  onError: { type: authRequestFailed.type, payload: { key: "profilePicTwo" } },
  cache: true,
  cacheDuration: 300000
})

export const UpdateUserDetails = (data) => apiCallBegan({
  url: config.UpdateProfileData,
  method: "post",
  data: data,
  onStart: { type: authRequestStarted.type, payload: "updateProfile" },
  onSuccess: userDetailsUpdated.type,
  onError: { type: authRequestFailed.type, payload: { key: "updateProfile" } },
});

export const updateUserProfilePic = (data) => apiCallBegan({
  url: config.UploadProfilePic,
  method: "post",
  data: data,
  onSuccess: userProfilePicUpdated.type
});

export const AddHostDetails = (data) => apiCallBegan({
  url: config.AddHostDetails,
  method: "post",
  data: data,
  onSuccess: hostDetailsAdded.type
});

export const loginStatusUpdate = (id) => apiCallBegan({
  url: config.UpdateLoginStatus + id,
  onSuccess: loginStatusDataUpdated.type
});

export const loginLogoutUpdateStatus = (id) => apiCallBegan({
  url: config.UpdateLoginLogoutStatus + id + "&type=logout",
  onSuccess: loginLogoutStatusReceived.type
});

export const SendOTPEmail = (username, email) => apiCallBegan({
  url: config.SendSecurityCodeToEmail + username + "&email=" + email,
  onSuccess: emailOtpReceived.type
});

export const VerifyOTPEmail = (email, code) => apiCallBegan({
  url: config.VerifySecurityCode + email + "&code=" + code + "&type=identity",
  onSuccess: verifiedOtp.type
});

export const loadClientDetails = () => apiCallBegan({
  url: config.GetClientDetails,
  onStart: { type: authRequestStarted.type, payload: "clientDetails" },
  onSuccess: clientDetailsRecevied.type,
  onError: { type: authRequestFailed.type, payload: { key: "clientDetails" } },
  cache: true,
  cacheDuration: 300000
});

// --- Account recovery / identity verification (Auth flow components) ---
// These thunks return the response payload directly (see the api middleware),
// so callers can `await dispatch(...)` and branch on the result, replacing the
// previous direct HttpService (http.get/http.post) calls in the Auth screens.

export const authStart = (validId, portalType = "workbench") =>
  apiCallBegan({
    url: config.AuthStart || "Auth/start",
    method: "post",
    data: { validId, portalType },
    withCredentials: true,
    throwOnError: true,
    silentError: true,
  });

export const getUserType = (validId) =>
  apiCallBegan({
    url: config.GetUserType + validId,
    throwOnError: true,
    silentError: true,
  });

export const verifyUserByUserName = (data) =>
  apiCallBegan({
    url: config.VerifyUserByUserName,
    method: "post",
    data,
    silentError: true,
  });

export const verifyUserByEmail = (data) =>
  apiCallBegan({
    url: config.VerifyUserByEmail,
    method: "post",
    data,
    silentError: true,
  });

export const sendSecurityCodeToEmail = (userName, email, type) =>
  apiCallBegan({
    url:
      config.SendSecurityCodeToEmail +
      userName +
      "&email=" +
      email +
      "&type=" +
      type,
    silentError: true,
  });

export const sendSecurityCodeToPhone = (userName, phone, type) =>
  apiCallBegan({
    url:
      config.SendSecurityCodeToPhone +
      userName +
      "&phone=" +
      encodeURIComponent(phone) +
      "&type=" +
      type,
    silentError: true,
  });

export const verifySecurityCode = (email, code, type) =>
  apiCallBegan({
    url: config.VerifySecurityCode + email + "&code=" + code + "&type=" + type,
    silentError: true,
  });

export const resetUserPassword = (data) =>
  apiCallBegan({
    url: config.ResetPassword,
    method: "post",
    data,
    silentError: true,
  });

export const verifySecurityQuestion = (secAnswer) =>
  apiCallBegan({
    url: config.VerifySecQuestion,
    method: "post",
    data: secAnswer,
    silentError: true,
  });

export const getLoggedUser = createSelector(
  (state) => state.auth,
  (auth) => auth
);

export const getLoggedUserDetails = createSelector(
  (state) => state.auth,
  (auth) => auth.userDetails
);

export const getLoggedUserToken = createSelector(
  (state) => state.auth,
  (auth) => auth.token
);

export const getUserUpdateStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.updateStatus
);

export const getOtpSentStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.emailOtpSentStatus
);

export const getOtpVerifyStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.verifyOtpStatus
);

export const getValidateStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.validateStatus
);

export const getLoginFailedStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.loginFailedStatus
);

export const getPicUpdatedStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.picUpdatedStatus
);
export const getUserProfilePic = createSelector(
  (state) => state.auth,
  (auth) => auth.profilePic
);
export const getUserProfilePicTwo = createSelector(
  (state) => state.auth,
  (auth) => auth.profilePicTwo
);

export const getloginupdatedstatus = createSelector(
  (state) => state.auth,
  (auth) => auth.updateddatarecieved
);

export const getHostDetails = createSelector(
  (state) => state.auth,
  (auth) => auth.hostDetails
);

export const getLogoutUserStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.logoutStatus
);

export const getLoginLogoutUserStatus = createSelector(
  (state) => state.auth,
  (auth) => auth.loginLogoutStatus
);

export const getCurrentClientDeatils = createSelector(
  (state) => state.auth,
  (auth) => auth.clientDetails
);

export const getAuthRequests = createSelector(
  (state) => state.auth,
  (auth) => auth.requests
);

export const getCurrentUser = () => {
  try {
    const tk = localStorage.getItem(tokenKey);
    const decode = jwt(tk);
    const logType = localStorage.getItem("loginType");
    if (logType === "ad") {
      decode.id = 1;
      decode.type = "admin";
    }
    return decode;
  } catch (error) {
    return null;
  }
};


